import type { ReactNode } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Logo } from './Icon.jsx';

export const STEPS = ['Photo', 'Check', 'Split', 'Result'] as const;
export type Step = (typeof STEPS)[number];

interface ShellProps {
  step?: Step;
  title: string;
  lede?: ReactNode;
  back?: string | true;
  children: ReactNode;
  /** Rendered in a bar pinned to the bottom, above the phone's home indicator. */
  actions?: ReactNode;
}

/**
 * One screen = one Shell. Header with progress, scrolling body, sticky
 * actions. Keeps every screen's primary button under the thumb.
 */
export function Shell({ step, title, lede, back, children, actions }: ShellProps): React.ReactElement {
  const navigate = useNavigate();
  const index = step ? STEPS.indexOf(step) : -1;

  return (
    <div className="shell">
      <header className="top">
        {back ? (
          <button
            type="button"
            className="back"
            aria-label="Back"
            onClick={() => (back === true ? navigate(-1) : navigate(back))}
          >
            ‹
          </button>
        ) : (
          <Link to="/" className="brand" aria-label="Home">
            <Logo />
          </Link>
        )}
        {index >= 0 && (
          <ol className="steps" aria-label="Progress">
            {STEPS.map((name, i) => (
              <li
                key={name}
                className={i < index ? 'done' : i === index ? 'current' : ''}
                aria-current={i === index ? 'step' : undefined}
              >
                <span>{name}</span>
              </li>
            ))}
          </ol>
        )}
      </header>

      <main className="body">
        <h1>{title}</h1>
        {lede && <p className="lede">{lede}</p>}
        {children}
      </main>

      {actions && <footer className="actions">{actions}</footer>}
    </div>
  );
}

export function Spinner({ label }: { label: string }): React.ReactElement {
  return (
    <div className="spinner" role="status" aria-live="polite">
      <span className="dot" />
      <span>{label}</span>
    </div>
  );
}

export function ErrorBox({ title, details }: { title: string; details?: string[] }): React.ReactElement {
  return (
    <div className="errorbox" role="alert">
      <p>{title}</p>
      {details && details.length > 0 && (
        <ul>
          {details.map((detail, i) => (
            <li key={i}>{detail}</li>
          ))}
        </ul>
      )}
    </div>
  );
}
